
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Info } from 'lucide-react';

interface CaseInfoSidePanelProps {
  patientId: string;
}

interface PatientCase {
  id: string;
  title: string;
  description: string | null;
  status: string;
  created_at: string;
  has_diabetes: boolean;
  has_hypertension: boolean;
  has_heart_disease: boolean;
  has_asthma: boolean;
  has_allergies: boolean;
  other_conditions: string | null;
}

const CaseInfoSidePanel: React.FC<CaseInfoSidePanelProps> = ({ patientId }) => {
  const [patientCase, setPatientCase] = useState<PatientCase | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPatientCase = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('patient_cases')
          .select('*')
          .eq('patient_id', patientId)
          .order('created_at', { ascending: false })
          .maybeSingle();
          
        if (error) {
          throw error;
        }
        
        setPatientCase(data);
      } catch (error: any) {
        console.error('Error fetching patient case:', error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPatientCase();
  }, [patientId]);

  if (isLoading) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Case Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-4">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span>Loading case information...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!patientCase) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Case Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center p-4">
            <Info className="h-12 w-12 text-muted-foreground mb-2" />
            <p className="text-center">No case information available for this patient.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatConditions = () => {
    const conditions = [];
    
    if (patientCase.has_diabetes) conditions.push('Diabetes');
    if (patientCase.has_hypertension) conditions.push('Hypertension');
    if (patientCase.has_heart_disease) conditions.push('Heart Disease');
    if (patientCase.has_asthma) conditions.push('Asthma');
    if (patientCase.has_allergies) conditions.push('Allergies');
    
    if (patientCase.other_conditions) {
      conditions.push(patientCase.other_conditions);
    }
    
    return conditions.length > 0 ? conditions.join(', ') : 'None reported';
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Case Information</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg">{patientCase.title}</h3>
            <p className="text-sm text-muted-foreground">
              Submitted: {new Date(patientCase.created_at).toLocaleDateString()}
            </p>
          </div>
          
          {patientCase.description && (
            <div>
              <h4 className="font-medium">Description</h4>
              <p className="text-sm">{patientCase.description}</p>
            </div>
          )}
          
          <div>
            <h4 className="font-medium">Medical Conditions</h4>
            <p className="text-sm">{formatConditions()}</p>
          </div>
          
          <div>
            <h4 className="font-medium">Status</h4>
            <p className="text-sm capitalize">{patientCase.status.replace('_', ' ')}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CaseInfoSidePanel;
